// ─────────────────────────────────────────────────────────────────────────────
// OarlockDebugPanel.js — Generic live-data panel for smart-gate oarlocks.
//
// Listens to `crewz-oarlock-debug-frame` (vendor-agnostic) and falls back to
// `crewz-nk-debug-frame` for backward compatibility with any older event
// dispatch path. For each frame we render:
//   • raw N-byte hex dump
//   • per-field raw value + scaled value + valid/null/invalid indicator
//   • side identifier and time delta since the previous frame
//
// Vendor banner: as of 2026-06 NK EmPower is REMOVED (NK ecosystem is closed
// — both EmPower and SpeedCoach advertise ADV_SCAN_IND non-connectable).
// XBoat XPod integration is pending GATT documentation. This panel is the
// shared surface they will both render into once docs arrive.
//
// Capture controls:
//   • REC · 20s   — buffers every frame for 20s then saves a JSON file
//   • SNAP · N    — copies the rolling last 50 frames as JSON to clipboard
// Toggle persists in localStorage as `crewz_nk_debug_panel = '1'` (key
// preserved for backward compatibility; rename deferred to avoid breaking
// existing user preferences).
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState } from 'react';
import { X, Activity, Clipboard, Check, Circle, Download } from 'lucide-react';

const NULL_U8 = 0x80;
const isNullU16 = (v) => v === 0x0000 || v === 0xFFFF;

// Description of every field in the dataStreamB payload — ordered to match
// the 19-byte layout so the debug grid reads top-to-bottom.
const FIELDS = [
  { id: 'timestampMs',        label: 'Timestamp',          unit: 'ms',  rawOffset: 0,  rawSize: 2 },
  { id: 'strokePeriodMs',     label: 'Stroke Period',      unit: 'ms',  rawOffset: 2,  rawSize: 2 },
  { id: 'spm',                label: 'Stroke Rate (SPM)',  unit: 'spm', derived: true },
  { id: 'driveTimeMs',        label: 'Drive Time',         unit: 'ms',  rawOffset: 4,  rawSize: 2 },
  { id: 'recoveryTimeMs',     label: 'Recovery Time',      unit: 'ms',  derived: true },
  { id: 'peakForceN',         label: 'Peak Force',         unit: 'N',   rawOffset: 10, rawSize: 2, scale: 0.01 },
  { id: 'avgForceN',          label: 'Avg Force',          unit: 'N',   rawOffset: 12, rawSize: 2, scale: 0.1 },
  { id: 'workPerStrokeJ',     label: 'Work / Stroke',      unit: 'J',   rawOffset: 6,  rawSize: 2, scale: 0.1 },
  { id: 'powerW',             label: 'Power',              unit: 'W',   rawOffset: 14, rawSize: 2, scale: 0.01 },
  { id: 'driveLengthCm',      label: 'Drive Length',       unit: 'cm',  rawOffset: 8,  rawSize: 2, scale: 0.1 },
  { id: 'catchAngleDeg',      label: 'Catch Angle',        unit: '°',   rawOffset: 16, rawSize: 1 },
  { id: 'finishAngleDeg',     label: 'Finish Angle',       unit: '°',   rawOffset: 17, rawSize: 1 },
  { id: 'effectiveLengthDeg', label: 'Effective Length',   unit: '°',   rawOffset: 18, rawSize: 1 },
  { id: 'isValid',            label: 'Frame Valid?',       unit: '',    derived: true },
];

function readRaw(bytes, offset, size) {
  if (!bytes || bytes.length < offset + size) return null;
  if (size === 1) return bytes[offset];
  // u16 LE
  return bytes[offset] | (bytes[offset + 1] << 8);
}

function isNullRaw(raw, size) {
  if (raw == null) return true;
  if (size === 1) return raw === NULL_U8;
  return isNullU16(raw);
}

function formatRaw(raw, size) {
  if (raw == null) return '—';
  const hex = raw.toString(16).padStart(size === 1 ? 2 : 4, '0');
  return `0x${hex} (${raw})`;
}

function formatScaled(parsed, field) {
  const v = parsed?.[field.id];
  if (v == null) return '—';
  if (typeof v === 'boolean') return v ? 'YES' : 'NO';
  if (typeof v === 'number') {
    return Number.isInteger(v) ? String(v) : v.toFixed(2);
  }
  return String(v);
}

export default function OarlockDebugPanel({ open, onClose }) {
  const [lastFrame, setLastFrame] = useState(null);
  const [frameCount, setFrameCount] = useState(0);
  const [delta, setDelta] = useState(0);
  const [copied, setCopied] = useState(false);
  // 20-second timed capture state — separate from the ring buffer above.
  // Captures EVERY frame received during the window (capped at 2000 frames)
  // and saves to a JSON file on the device when the window closes.
  const [recording, setRecording] = useState(false);
  const [recordSecondsLeft, setRecordSecondsLeft] = useState(0);
  const [savedFilename, setSavedFilename] = useState(null);
  const recordBufferRef = useRef([]);
  const recordStartRef = useRef(0);
  const recordTimeoutRef = useRef(null);
  const recordTickRef = useRef(null);
  const CAPTURE_SECONDS = 20;
  const CAPTURE_FRAME_CAP = 2000;
  const prevTsRef = useRef(0);
  // Ring buffer of the last 50 frames — kept in a ref so frame storms don't
  // re-render the panel on every notification. Snapshot button serialises it
  // to JSON and copies to the clipboard.
  const frameBufferRef = useRef([]);
  const FRAME_BUFFER_LIMIT = 50;

  useEffect(() => {
    if (!open) return undefined;
    const onFrame = (event) => {
      const d = event?.detail;
      if (!d) return;
      const now = d.t || Date.now();
      setDelta(prevTsRef.current ? now - prevTsRef.current : 0);
      prevTsRef.current = now;
      setLastFrame(d);
      setFrameCount(c => c + 1);
      const entry = {
        t: now,
        side: d.side,
        rawHex: d.rawHex,
        rawBytes: d.rawBytes,
        parsed: d.parsed,
      };
      const buf = frameBufferRef.current;
      buf.push(entry);
      if (buf.length > FRAME_BUFFER_LIMIT) {
        buf.splice(0, buf.length - FRAME_BUFFER_LIMIT);
      }
      // Mirror into the timed capture buffer when recording.
      if (recordingRef.current && recordBufferRef.current.length < CAPTURE_FRAME_CAP) {
        recordBufferRef.current.push(entry);
      }
    };
    window.addEventListener('crewz-oarlock-debug-frame', onFrame);
    // Backward compatibility — keep listening to the legacy NK-specific event
    // for any code path that hasn't been migrated yet.
    window.addEventListener('crewz-nk-debug-frame', onFrame);
    return () => {
      window.removeEventListener('crewz-oarlock-debug-frame', onFrame);
      window.removeEventListener('crewz-nk-debug-frame', onFrame);
    };
  }, [open]);

  // Track recording state in a ref so the frame handler reads the latest value
  // without re-subscribing each render (would drop in-flight frames).
  const recordingRef = useRef(false);
  useEffect(() => { recordingRef.current = recording; }, [recording]);

  // Cleanup any in-flight timers when the panel closes or unmounts.
  useEffect(() => () => {
    if (recordTimeoutRef.current) clearTimeout(recordTimeoutRef.current);
    if (recordTickRef.current) clearInterval(recordTickRef.current);
  }, []);

  // Build a snapshot envelope from any frame array — reused by both the
  // clipboard SNAPSHOT button and the timed capture file save.
  const buildSnapshot = (frames, meta = {}) => ({
    app: 'CrewZ',
    panel: 'NKEmpowerDebugPanel',
    schemaVersion: 1,
    capturedAt: new Date().toISOString(),
    frameCount: frames.length,
    frames,
    ...meta,
  });

  // Save a JSON blob to the user's device. Prefers File System Access API
  // (modern Chrome / Android Chrome — lets the user pick Documents) and
  // falls back to a synthetic <a download> link (lands in Downloads).
  const saveJsonFile = async (snapshot, filename) => {
    const json = JSON.stringify(snapshot, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    // Path 1 — modern File System Access API
    try {
      if (typeof window.showSaveFilePicker === 'function') {
        const handle = await window.showSaveFilePicker({
          suggestedName: filename,
          types: [{ description: 'CrewZ NK Capture (JSON)', accept: { 'application/json': ['.json'] } }],
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        return true;
      }
    } catch (_e) { /* user cancelled OR unsupported — fall through */ }
    // Path 2 — legacy anchor download (Downloads folder on Android)
    try {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      return true;
    } catch (_e) {
      return false;
    }
  };

  const finishRecording = async () => {
    if (recordTickRef.current) { clearInterval(recordTickRef.current); recordTickRef.current = null; }
    if (recordTimeoutRef.current) { clearTimeout(recordTimeoutRef.current); recordTimeoutRef.current = null; }
    setRecording(false);
    setRecordSecondsLeft(0);
    const frames = recordBufferRef.current.slice();
    const durationMs = Date.now() - recordStartRef.current;
    const snapshot = buildSnapshot(frames, {
      captureMode: 'timed',
      captureDurationMs: durationMs,
      captureRequestedSec: CAPTURE_SECONDS,
      capturedFrames: frames.length,
      cappedAt: CAPTURE_FRAME_CAP,
    });
    const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const filename = `crewz-nk-capture-${ts}.json`;
    const ok = await saveJsonFile(snapshot, filename);
    if (ok) setSavedFilename(filename);
    setTimeout(() => setSavedFilename(null), 5000);
  };

  const startRecording = (e) => {
    if (e) e.stopPropagation();
    if (recording) return;
    recordBufferRef.current = [];
    recordStartRef.current = Date.now();
    setRecording(true);
    setRecordSecondsLeft(CAPTURE_SECONDS);
    setSavedFilename(null);
    // Tick the visible countdown every 1s.
    recordTickRef.current = setInterval(() => {
      setRecordSecondsLeft(s => {
        const next = s - 1;
        return next < 0 ? 0 : next;
      });
    }, 1000);
    // Hard stop at exactly CAPTURE_SECONDS seconds.
    recordTimeoutRef.current = setTimeout(() => { finishRecording(); }, CAPTURE_SECONDS * 1000);
  };

  // Build a JSON snapshot of the buffered frames + metadata. Wrapped in a
  // try/catch and falls back to a manual textarea-copy when the Clipboard
  // API is unavailable (e.g. older Android Chrome over http).
  const copySnapshot = async (e) => {
    if (e) e.stopPropagation();
    const snapshot = buildSnapshot(frameBufferRef.current.slice(), { captureMode: 'rolling-50' });
    const json = JSON.stringify(snapshot, null, 2);
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(json);
        ok = true;
      } else {
        const ta = document.createElement('textarea');
        ta.value = json;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand('copy');
        document.body.removeChild(ta);
      }
    } catch (_err) {
      ok = false;
    }
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  if (!open) return null;

  const parsed = lastFrame?.parsed;
  const rawBytes = lastFrame?.rawBytes || [];
  const rawHex = lastFrame?.rawHex || '—';
  const side = lastFrame?.side || '—';

  return (
    <div
      data-testid="oarlock-debug-panel"
      className="fixed bottom-3 right-3 z-50 w-[420px] max-w-[95vw] max-h-[80vh] overflow-y-auto rounded-xl bg-zinc-950 text-zinc-100 border-2 border-emerald-500/60 shadow-2xl"
      style={{ fontFamily: 'JetBrains Mono, monospace' }}
      onClick={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-800 bg-emerald-950/50">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-emerald-400" />
          <div>
            <div className="text-xs font-bold tracking-wider text-emerald-300">OARLOCK · DEBUG</div>
            <div className="text-[10px] text-zinc-400">side: {side} · frames: {frameCount} · Δ {delta}ms</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={startRecording}
            disabled={recording}
            data-testid="nk-debug-record-btn"
            className={`px-2 py-1 rounded text-[10px] font-bold tracking-wider flex items-center gap-1 transition-colors ${
              recording
                ? 'bg-rose-600 text-white animate-pulse cursor-default'
                : 'bg-rose-500 hover:bg-rose-400 text-white'
            }`}
            title={recording ? `Recording — ${recordSecondsLeft}s remaining` : `Record ${CAPTURE_SECONDS}s capture and save to device`}
            aria-label="Start 20-second capture"
          >
            <Circle className={`w-3 h-3 ${recording ? 'fill-white' : 'fill-white'}`} />
            {recording ? `REC · ${String(recordSecondsLeft).padStart(2, '0')}s` : `REC · ${CAPTURE_SECONDS}s`}
          </button>
          <button
            onClick={copySnapshot}
            data-testid="nk-debug-share-snapshot"
            disabled={frameBufferRef.current.length === 0}
            className={`px-2 py-1 rounded text-[10px] font-bold tracking-wider flex items-center gap-1 transition-colors ${
              frameBufferRef.current.length === 0
                ? 'bg-zinc-800 text-zinc-600 cursor-not-allowed'
                : copied
                  ? 'bg-emerald-500 text-zinc-950'
                  : 'bg-emerald-600 hover:bg-emerald-500 text-white'
            }`}
            title="Copy last 50 frames as JSON to clipboard"
            aria-label="Share debug snapshot"
          >
            {copied ? <Check className="w-3 h-3" /> : <Clipboard className="w-3 h-3" />}
            {copied ? 'COPIED' : `SNAP · ${frameBufferRef.current.length}`}
          </button>
          <button
            onClick={onClose}
            data-testid="nk-debug-close"
            className="p-1 rounded hover:bg-zinc-800"
            aria-label="Close NK debug panel"
          >
            <X className="w-4 h-4 text-zinc-400" />
          </button>
        </div>
      </div>

      {/* Vendor-pivot banner (2026-06) — NK ecosystem is closed; XBoat
          documentation is pending. */}
      <div
        data-testid="oarlock-debug-banner"
        className="px-3 py-2 border-b border-zinc-800 bg-amber-900/30 text-[11px] text-amber-200 leading-snug"
      >
        <span className="font-bold text-amber-100">NK EmPower:</span> Not supported.
        Awaiting XBoat XPod integration.
      </div>

      <div className="px-3 py-2 border-b border-zinc-800">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Raw hex ({rawBytes.length} B)</div>
        <div className="text-[11px] text-emerald-300 break-all leading-snug">
          {rawHex}
        </div>
      </div>

      <div className="px-3 py-2">
        <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-2 gap-y-1 text-[11px]">
          <div className="text-zinc-500 font-semibold">Field</div>
          <div className="text-zinc-500 font-semibold text-right">Raw</div>
          <div className="text-zinc-500 font-semibold text-right">Scaled</div>
          <div className="text-zinc-500 font-semibold text-right">State</div>
          {FIELDS.map(f => {
            let rawText = '—';
            let nullish = false;
            if (!f.derived) {
              const r = readRaw(rawBytes, f.rawOffset, f.rawSize);
              rawText = formatRaw(r, f.rawSize);
              nullish = isNullRaw(r, f.rawSize);
            } else {
              const v = parsed?.[f.id];
              nullish = v == null || (typeof v === 'number' && v === 0 && f.id !== 'isValid');
              rawText = '(derived)';
            }
            const scaledText = formatScaled(parsed, f);

            // State precedence: INVALID (red) > NULL (amber) > OK (green).
            // INVALID applies to ALL fields when the frame itself is rejected
            // by parseStrokeFrame.isValid — even non-null scaled values are
            // suspect because the underlying transition wasn't a real stroke.
            // `isValid` itself stays green/amber so the rower can read it.
            const frameInvalid = parsed && parsed.isValid === false && f.id !== 'isValid';
            const state = frameInvalid ? 'INVALID' : (nullish ? 'NULL' : 'OK');
            const stateColor = state === 'INVALID'
              ? 'text-rose-400'
              : state === 'NULL'
                ? 'text-amber-400'
                : 'text-emerald-400';
            return (
              <div key={f.id} className="contents">
                <div className="text-zinc-200">
                  {f.label}
                  {f.unit && <span className="text-zinc-600"> · {f.unit}</span>}
                </div>
                <div className="text-right text-zinc-300">{rawText}</div>
                <div className={`text-right ${state === 'OK' ? 'text-emerald-200' : 'text-zinc-500'}`}>{scaledText}</div>
                <div className={`text-right font-bold ${stateColor}`}>{state}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="px-3 py-2 border-t border-zinc-800 text-[10px] text-zinc-500 space-y-1">
        {recording && (
          <div className="flex items-center gap-1.5 text-rose-300 font-bold">
            <Circle className="w-2.5 h-2.5 fill-rose-400 animate-pulse" />
            RECORDING · {String(recordSecondsLeft).padStart(2, '0')}s remaining · {recordBufferRef.current.length} frames buffered
          </div>
        )}
        {savedFilename && !recording && (
          <div className="flex items-center gap-1.5 text-emerald-300 font-bold">
            <Download className="w-2.5 h-2.5" />
            SAVED: {savedFilename}
          </div>
        )}
        <div>
          Listening to <span className="text-emerald-300">crewz-oarlock-debug-frame</span> (and legacy NK event).
        </div>
        <div>
          <span className="text-emerald-300">REC · {CAPTURE_SECONDS}s</span> records every frame and saves to your device (you'll be asked where).
        </div>
        <div>
          <span className="text-emerald-300">SNAP</span> copies the rolling last 50 frames as JSON to the clipboard.
        </div>
      </div>
    </div>
  );
}
